/**
 * The bilingual marketing copy attached to a sellable plan.
 *
 * WHY IT LIVES ON THE PLAN AND NOT IN THE SITE'S COPY DOCUMENT. The public
 * pricing band reads the plans a tenant is actually billed against, so its
 * figures — the price, the currency, the three ceilings — cannot be invented or
 * go stale. The sentences beside those figures still can, and they belong to
 * the same row: change a plan's price on this screen and its card changes on
 * tarabeeza.com, and the line saying who the plan is for should move with it.
 *
 * BOTH LANGUAGES OR THE PLAN IS NOT PUBLISHED. The API refuses to store a
 * half-written block and the site leaves an incomplete plan off the page. That
 * is the same no-fallback rule the rest of the site holds to: an Arabic card
 * with a name, a price and an empty body is worse than a card that is not there.
 */

export const MARKETING_LOCALES = ['en', 'ar'] as const;
export type MarketingLocale = (typeof MARKETING_LOCALES)[number];

export interface PlanMarketingCopy {
  /** What the card calls the plan in this language — not the operator's label. */
  name: string;
  audience: string;
  features: string[];
}

export type PlanMarketing = Record<MarketingLocale, PlanMarketingCopy> & {
  /** Draws this plan as the recommended one. The same decision in both languages. */
  featured?: boolean;
  /**
   * The struck-through "was" price, when the plan is sold as an offer.
   *
   * NOT A COLUMN AND NOT A PRICE ANYONE IS BILLED. `priceMonthly` stays the one
   * figure on the invoice; this is a claim about what the plan would otherwise
   * cost, so it is editorial and it is written here, on the screen where the
   * card's other words are written. A decimal string for the same reason every
   * price in this codebase is one.
   */
  listPriceMonthly?: string;
  /** The line explaining the offer. A sentence, so both languages or neither. */
  offer?: Record<MarketingLocale, string>;
};

export const BLANK_COPY: PlanMarketingCopy = { name: '', audience: '', features: [''] };

export const BLANK_MARKETING: PlanMarketing = {
  featured: false,
  listPriceMonthly: '',
  offer: { en: '', ar: '' },
  en: { ...BLANK_COPY, features: [''] },
  ar: { ...BLANK_COPY, features: [''] },
};

/** Read what a plan row holds into something the form can edit. */
export function toForm(value: unknown): PlanMarketing {
  const source = (value ?? {}) as Record<string, unknown>;
  const read = (locale: MarketingLocale): PlanMarketingCopy => {
    const copy = (source[locale] ?? {}) as Record<string, unknown>;
    const features = Array.isArray(copy.features)
      ? (copy.features as unknown[]).map((line) => (typeof line === 'string' ? line : ''))
      : [];
    return {
      name: typeof copy.name === 'string' ? copy.name : '',
      audience: typeof copy.audience === 'string' ? copy.audience : '',
      // Never zero rows: an empty list gives the form nothing to type into.
      features: features.length ? features : [''],
    };
  };
  const text = (value: unknown): string => (typeof value === 'string' ? value : '');
  const offer = (source.offer ?? {}) as Record<string, unknown>;
  return {
    featured: source.featured === true,
    /* Always a string, never absent: an input whose value flips between
       undefined and a string is the uncontrolled-component warning, and React
       stops reporting what the operator types. Absence is expressed on the way
       out instead, by `toPayload`. */
    listPriceMonthly: text(source.listPriceMonthly),
    offer: { en: text(offer.en), ar: text(offer.ar) },
    en: read('en'),
    ar: read('ar'),
  };
}

/**
 * The form's value, trimmed and with empty feature rows dropped, ready to send.
 *
 * A blank row is how someone leaves a list they were part-way through, not a
 * feature they meant to publish as an empty bullet. Dropping it here means the
 * form can always show one spare row without that row becoming a refusal.
 */
export function toPayload(form: PlanMarketing): PlanMarketing {
  const clean = (copy: PlanMarketingCopy): PlanMarketingCopy => ({
    name: copy.name.trim(),
    audience: copy.audience.trim(),
    features: copy.features.map((line) => line.trim()).filter(Boolean),
  });
  const listPriceMonthly = form.listPriceMonthly?.trim() ?? '';
  const offer = { en: form.offer?.en.trim() ?? '', ar: form.offer?.ar.trim() ?? '' };
  return {
    featured: form.featured === true,
    /* An empty field is a plan with no offer, not a plan offering nothing —
       sending `''` would have the API refuse a price that was never typed. Half
       an offer note IS sent, so the refusal names the language that is short
       rather than silently dropping the half somebody did write. */
    ...(listPriceMonthly ? { listPriceMonthly } : {}),
    ...(offer.en || offer.ar ? { offer } : {}),
    en: clean(form.en),
    ar: clean(form.ar),
  };
}

/**
 * A non-negative price written the way money is written, and a comparison that
 * treats two of them as numbers.
 *
 * A MIRROR OF THE API'S OWN RULE, kept deliberately literal. `'99' > '199'` is
 * true to a string comparison and false to a reader, so the screen would accept
 * a "was" price that is really a price rise and let the server be the one to
 * say no.
 */
const DECIMAL_STRING = /^\d+(\.\d+)?$/;

function compareDecimals(left: string, right: string): number {
  const places = Math.max(left.split('.')[1]?.length ?? 0, right.split('.')[1]?.length ?? 0);
  const scale = (value: string): bigint => {
    const [whole, fraction = ''] = value.split('.');
    return BigInt(whole + fraction.padEnd(places, '0'));
  };
  const a = scale(left);
  const b = scale(right);
  return a === b ? 0 : a > b ? 1 : -1;
}

export type ListPriceProblem = 'notANumber' | 'notAnOffer';

/**
 * What is wrong with the struck-through price, if anything.
 *
 * IT HAS TO BE HIGHER THAN THE PLAN'S OWN PRICE. A struck-out figure reads as
 * "what you would otherwise pay"; one equal to the price beside it is a
 * discount of nothing, and one below it tells the reader the price went UP.
 * Neither is a thing the public card can draw honestly, so the API refuses
 * both — and answering here as well means the operator learns it while looking
 * at the field, with the plan's real price in front of them, instead of after
 * pressing Save.
 *
 * `priceMonthly` is the plan's own monthly price, as the row's decimal string.
 */
export function listPriceProblem(
  form: PlanMarketing,
  priceMonthly = '',
): ListPriceProblem | null {
  const listPrice = form.listPriceMonthly?.trim() ?? '';
  if (!listPrice) return null;
  if (!DECIMAL_STRING.test(listPrice)) return 'notANumber';
  // No plan price to measure against — refuse rather than pass it through
  // unchecked. Every screen here has the row in hand, so this is the guard
  // against a caller that forgot it, not a state an operator can type into.
  if (!DECIMAL_STRING.test(priceMonthly)) return 'notANumber';
  return compareDecimals(listPrice, priceMonthly) <= 0 ? 'notAnOffer' : null;
}

/**
 * Every reason this block would be refused, by field.
 *
 * A MIRROR OF THE API'S OWN CHECK, and it has to stay one — the server refuses
 * the same shapes with the same paths. It is repeated here so the answer
 * arrives while somebody is typing rather than after they press Save, which for
 * a bilingual form is the difference between fixing one field and re-reading
 * two languages to work out which one is short.
 *
 * `priceMonthly` is the plan's own price, needed because one of those shapes —
 * a struck-through "was" price — is only wrong in relation to it. Left out, no
 * price is knowable and a block claiming one is refused, which is what the API
 * does with the same omission: an unchecked reference price is the single thing
 * the rule exists to stop.
 */
export function marketingProblems(form: PlanMarketing, priceMonthly = ''): string[] {
  const payload = toPayload(form);
  const problems: string[] = [];
  if (listPriceProblem(form, priceMonthly)) problems.push('listPriceMonthly');
  // The note is a sentence, so it follows the copy's rule and not the price's:
  // one language written and the other blank is a card that explains its own
  // struck-out figure to half the audience.
  if (payload.offer) {
    for (const locale of MARKETING_LOCALES) {
      if (!payload.offer[locale]) problems.push(`offer.${locale}`);
    }
  }
  for (const locale of MARKETING_LOCALES) {
    const copy = payload[locale];
    if (!copy.name) problems.push(`${locale}.name`);
    if (!copy.audience) problems.push(`${locale}.audience`);
    if (copy.features.length === 0) problems.push(`${locale}.features`);
  }
  return problems;
}

/** Has anything actually changed against what the row holds? */
export function isDirty(form: PlanMarketing, original: unknown): boolean {
  return JSON.stringify(toPayload(form)) !== JSON.stringify(toPayload(toForm(original)));
}

/**
 * Does the marketing copy restate a ceiling the plan row already carries?
 *
 * A WARNING, NOT A REFUSAL, and it exists because the design this copy came
 * from did exactly that: "1 branch", "Up to 5 branches", "Unlimited branches"
 * — every one of them a second copy of `maxBranches`, printed beside the first
 * copy on the same card, free to drift the moment somebody changes the plan.
 * The card renders the ceilings from the row itself, so a feature line saying
 * the same thing is at best duplication and at worst a contradiction.
 */
const CEILING_WORDS = [/\bbranch(es)?\b/i, /\bdevices?\b/i, /\bstaff accounts?\b/i, /فرع|فروع/, /أجهزة/];

export function ceilingEchoes(form: PlanMarketing): string[] {
  const echoes: string[] = [];
  for (const locale of MARKETING_LOCALES) {
    form[locale].features.forEach((line, index) => {
      if (CEILING_WORDS.some((pattern) => pattern.test(line))) {
        echoes.push(`${locale}.features[${index}]`);
      }
    });
  }
  return echoes;
}
